<template>
  <div>
    <v-dialog v-model="display" :width="width" persistent lazy full-width>
      <v-text-field
        slot="activator"
        :label="label"
        :value="formattedDatetime"
        :box="!outline"
        :outline="outline"
        :disabled="disabled"
        :error-messages="getErrors(validator)"
        append-icon="date_range"
        readonly
        :hint="hint"
        persistent-hint
        @blur="validator && validator.$touch()"
      >
        <template v-if="$slots.append" slot="append">
          <slot name="append" />
        </template>
      </v-text-field>
      <v-card>
        <v-card-text style="padding: 0; height: 450px">
          <v-tabs v-model="activeTab" fixed-tabs>
            <v-tab key="calendar">
              <v-icon>event</v-icon>
            </v-tab>
            <v-tab key="timer">
              <v-icon>access_time</v-icon>
            </v-tab>
            <v-tab-item key="calendar">
              <v-date-picker
                v-model="datePart"
                :locale="isAdminLocalization ? $adminLocale() : locale"
                :min="min ? getDate(min) : undefined"
                :max="max ? getDate(max) : undefined"
                full-width
                scrollable
                actions
              />
            </v-tab-item>
            <v-tab-item key="timer">
              <v-time-picker
                ref="timer"
                v-model="timePart"
                :format="timePickerFormat"
                :min="min ? getTime(min) : undefined"
                :max="max ? getTime(max) : undefined"
                full-width
                class="v-time-picker-custom"
                scrollable
                actions
              />
            </v-tab-item>
          </v-tabs>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <main-button :text="cancelText === 'Cancel' ? adminText('shared.cancel', 'Cancel') : cancelText" flat @click.native="cancelHandler" />
          <main-button :text="okText === 'OK' ? adminText('shared.ok', 'OK') : okText" depressed @click.native="okHandler" />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import moment from 'moment'
import { defaultMessages, getValidationErrors } from './../../../utils'

const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD'
const DEFAULT_TIME_FORMAT = 'HH:mm'

export default {
  mixins: [AdminContext],
  name: 'datetime-picker',
  props: {
    outline: Boolean,
    minutePrecision: Boolean,
    validator: {
      type: Object,
      default: null,
    },
    errorMessages: {
      type: Object,
      default: null,
    },
    value: {
      type: [Date, String],
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    width: {
      type: Number,
      default: 320,
    },
    format: {
      type: String,
      default: 'dddd, MMMM Do YYYY, HH:mm',
    },
    timePickerFormat: {
      type: String,
      default: '24hr',
    },
    locale: {
      type: String,
      default: 'en-us',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    cancelText: {
      type: String,
      default: 'Cancel',
    },
    okText: {
      type: String,
      default: 'OK',
    },
    min: {
      type: String,
      default: undefined,
    },
    max: {
      type: String,
      default: undefined,
    },
    hint: {
      type: String,
      default: undefined,
    },
  },
  data() {
    this.value && this.$emit('input', new Date(this.value))

    return {
      display: false,
      timeSelected: false,
      activeTab: 0,
      selectedDatetime: this.value,
    }
  },
  computed: {
    datePart: {
      get() {
        const date = this.selectedDatetime
          ? new Date(this.selectedDatetime)
          : new Date(this.min || this.max || new Date().getTime())

        return moment(date).format(DEFAULT_DATE_FORMAT)
      },
      set(val) {
        this.activeTab = 1

        let date = moment(val, DEFAULT_DATE_FORMAT)
        let hour = this.selectedDatetime ? moment(this.selectedDatetime).hour() : 0
        let minute = this.selectedDatetime ? moment(this.selectedDatetime).minute() : 0
        let input = moment()
          .year(date.year())
          .month(date.month())
          .date(date.date())
          .hour(hour)
          .minute(minute)
          .second(0)
        this.selectedDatetime = input.toDate()
      },
    },
    timePart: {
      get() {
        const time = this.selectedDatetime
          ? new Date(this.selectedDatetime)
          : new Date(this.min || this.max || new Date().getTime())

        return moment(time).format(DEFAULT_TIME_FORMAT)
      },
      set(val) {
        this.timeSelected = true

        let time = moment(val, DEFAULT_TIME_FORMAT)
        let input = moment(this.selectedDatetime).hour(time.hour()).minute(time.minute()).second(0)
        this.selectedDatetime = input.toDate()
      },
    },
    formattedDatetime() {
      return this.value ? moment(new Date(this.value)).locale(this.isAdminLocalization ? this.$adminLocale() : this.locale).format(this.format) : ''
    },
  },
  watch: {
    display(val) {
      if (val) {
        this.selectedDatetime = this.selectedDatetime
          ? this.selectedDatetime
          : new Date(this.min || this.max || new Date().getTime())
      }
    },
  },
  methods: {
    getDate(input) {
      return moment(input).format(DEFAULT_DATE_FORMAT)
    },
    getTime(input) {
      if (moment(this.selectedDatetime).startOf('day').isSame(moment(input).startOf('day'))) {
        return moment(input).format(DEFAULT_TIME_FORMAT)
      }

      return undefined
    },
    okHandler() {
      this.display = false
      this.activeTab = 0
      this.selectedDatetime = this.selectedDatetime
        ? this.selectedDatetime
        : this.min || this.max || new Date()
      if (this.minutePrecision) {
        const selectedDatetime = new Date(this.selectedDatetime)
        selectedDatetime.setSeconds(0, 0)
        this.selectedDatetime = selectedDatetime
      }
      this.$emit('input', this.selectedDatetime)
    },
    getErrors: function (field) {
      return getValidationErrors({
        ...this.adminValidationMessages(defaultMessages),
        ...this.adminFieldValidationMessages(this.errorMessages),
      })(field)
    },
    cancelHandler() {
      this.display = false
      this.activeTab = 0
    },
  },
}
</script>

<style>
.v-time-picker-title {
  height: 56px !important;
}
.v-input--has-state .v-input__slot:before {
  border-color: rgb(219, 56, 76) !important;
}
</style>
